/* ─────────────────────────────────────────────────────────────
   reducer-date-filter.ts
   Keeps a single source-of-truth for the dashboard’s date range
   ───────────────────────────────────────────────────────────── */
import {Action} from 'redux';
import moment from 'moment';

/* ---------- action types ---------- */
export const DF_SET_PRESET_RANGE = 'dateFilter/SET_PRESET_RANGE' as const;
export const DF_SET_CUSTOM_RANGE = 'dateFilter/SET_CUSTOM_RANGE' as const;

/* ---------- public types ---------- */
export type PresetRange =
  | 'TODAY'
  | 'THIS_WEEK'
  | 'THIS_MONTH'
  | 'THIS_YEAR'
  | 'LAST_7_DAYS'
  | 'LAST_30_DAYS'
  | 'ALL_TIME'
  | 'CUSTOM';

export const presetRangeLabels: Record<PresetRange, string> = {
  TODAY: 'Today',
  THIS_WEEK: 'This week',
  THIS_MONTH: 'This month',
  THIS_YEAR: 'This year',
  LAST_7_DAYS: 'Last 7 days',
  LAST_30_DAYS: 'Last 30 days',
  ALL_TIME: 'All time',
  CUSTOM: 'Custom',
};

export interface IDateFilterState {
  preset: PresetRange; // which option is selected in the UI
  start: Date; // inclusive
  end: Date; // inclusive
}

/* ---------- helpers ---------- */
function reduxPresetToDates(preset: PresetRange): {start: Date; end: Date} {
  const now = moment();

  switch (preset) {
    case 'TODAY':
      return {
        start: now.startOf('day').toDate(),
        end: now.endOf('day').toDate(),
      };

    case 'THIS_WEEK':
      return {
        start: now.startOf('week').toDate(),
        end: now.endOf('week').toDate(),
      };

    case 'THIS_MONTH':
      return {
        start: now.startOf('month').toDate(),
        end: now.endOf('month').toDate(),
      };

    case 'THIS_YEAR':
      return {
        start: now.startOf('year').toDate(),
        end: now.endOf('year').toDate(),
      };

    case 'LAST_7_DAYS':
      return {
        start: now.clone().subtract(7, 'days').startOf('day').toDate(),
        end: now.endOf('day').toDate(),
      };

    case 'LAST_30_DAYS':
      return {
        start: now.clone().subtract(30, 'days').startOf('day').toDate(),
        end: now.endOf('day').toDate(),
      };

    case 'ALL_TIME':
      return {
        start: new Date('2017-01-01T00:00:00'),
        end: now.endOf('day').toDate(),
      };

    /* CUSTOM → leave dates unchanged (caller must supply them) */
    default:
      return {
        start: now.startOf('month').toDate(),
        end: now.endOf('month').toDate(),
      };
  }
}

/* ---------- initial state ---------- */
const initialDates = reduxPresetToDates('THIS_MONTH');

const initialState: IDateFilterState = {
  preset: 'THIS_MONTH',
  start: initialDates.start,
  end: initialDates.end,
};

/* ---------- reducer ---------- */
interface IAction<T = any> extends Action<string> {
  payload?: T;
}

export default function reduxDateFilter(
  state: IDateFilterState = initialState,
  action: IAction,
): IDateFilterState {
  switch (action.type) {
    case DF_SET_PRESET_RANGE: {
      const preset = action.payload as PresetRange;
      const {start, end} = reduxPresetToDates(preset);
      return {...state, preset, start, end};
    }

    case DF_SET_CUSTOM_RANGE: {
      const {start, end} = action.payload as {start: Date; end: Date};
      return {...state, preset: 'CUSTOM', start, end};
    }

    default:
      return state;
  }
}

/* ---------- action creators ---------- */
export const dfSetPresetRange = (preset: PresetRange) => ({
  type: DF_SET_PRESET_RANGE,
  payload: preset,
});

export const dfSetCustomRange = (start: Date, end: Date) => ({
  type: DF_SET_CUSTOM_RANGE,
  payload: {start, end},
});
